import {
  Button,
  InlineLink,
  Loading,
  TextLargeParagraphTitle,
} from '@carboncopyco/ui';
import { useAnalytics } from '@carboncopyco/utils';
import { useEffect, useState } from 'react';

import {
  HandLeftBlueIllustration,
  HandRightYellowIllustration,
  WelcomeDemostackLogoIllustration,
} from '@carboncopyco/illustrations';
import { WindowType } from '@carboncopyco/types/desktop';
import { useCountDown } from 'ahooks';
import { settings } from '../../../../settings';
import { AnalyticsEvents } from '../../../shared/constants/analytics.events';
import { AppEventsEnum } from '../../../shared/enums/AppEventsEnum';
import WindowsTitleBar from '../../components/WindowsTitleBar/WindowsTitleBar';
import usePageLoadedAnimation from '../../hooks/usePageLoadedAnimation';
import { openLinkInBrowser } from '../../shared/utils';

const ButtonClickTimeOut = 20 * 1000;

const LoginScreen = () => {
  const { trackEvent } = useAnalytics();

  const { animationClasses, animatedStyles } = usePageLoadedAnimation();
  const [targetDate, setTargetDate] = useState<number | null>();
  const [countdown] = useCountDown({
    targetDate,
  });
  const [visibleBackground, setVisibleBackground] = useState(true);

  useEffect(() => {
    setVisibleBackground(true);
    window?.dsDesktop?.app?.updateMenuResetStates(false);
    window?.dsDesktop?.view.resizeByType(WindowType.LOGIN);
    return () => setVisibleBackground(false);
  }, []);

  const handleLogin = async () => {
    if (countdown !== 0) return;
    window.focus();
    setTargetDate(Date.now() + ButtonClickTimeOut);
    trackEvent(AnalyticsEvents.CLICK_LOGIN, {
      location: 'login_page',
    });
    try {
      const challengeCode = await window?.dsDesktop?.user.getCodeChallenge();
      openLinkInBrowser(
        null,
        `${settings.WEB_APP}/login/desktop/${challengeCode}`
      );
    } catch (error) {
      setTargetDate(null);
      throw new Error(JSON.stringify(error));
    }
  };

  useEffect(() => {
    if (!visibleBackground) {
      window.dsDesktop.view.resizeByType(WindowType.MAIN);
    }
  }, [visibleBackground]);

  useEffect(() => {
    window.dsDesktop.ipcRenderer.on(
      AppEventsEnum.MAIN_TO_APP_LOGIN_SUCCESS,
      () => {
        setVisibleBackground(false);
      }
    );
  }, []);

  return visibleBackground ? (
    <div
      className={`flex w-full h-full justify-between pt-[73px]  ${animationClasses}`}
      style={animatedStyles}
    >
      <HandLeftBlueIllustration />
      <div className="flex flex-col items-center justify-between pt-[150px]">
        <div className="flex flex-col justify-center items-center gap-4">
          <TextLargeParagraphTitle
            label="Welcome to"
            color="text-ds-natural-600"
          />
          <WelcomeDemostackLogoIllustration />
          <div className="pt-4 [&_button]:w-[300px]">
            <Button
              leftIcon={
                countdown !== 0 ? (
                  <Loading
                    color="text-current"
                    size={16}
                  />
                ) : undefined
              }
              circle
              label="Login with Browser"
              onClick={handleLogin}
            />
          </div>
        </div>
        <InlineLink
          letterSpacing="tracking-ds-normal"
          lineHeight="leading-ds-200"
          fontSize="text-ds-200"
          label="Terms of service"
          color="text-ds-natural-500"
          className="pb-8"
          onClick={(e) =>
            openLinkInBrowser(e, 'https://www.demostack.com/termsandconditions')
          }
        />
      </div>
      <div className="pt-[170px]">
        <HandRightYellowIllustration />
      </div>
      <div className="draggable top-0 left-0 w-full h-9 absolute flex" />

      {window?.dsDesktop?.app?.isWindows && (
        <div className="no-drag top-0 right-0 h-9 absolute flex">
          <WindowsTitleBar iconsColor="text-ds-natural-300" />
        </div>
      )}
    </div>
  ) : null;
};

export default LoginScreen;
